import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from "react";
import styles from "./GridGallery.module.css";
import ContentTitle from "../sidesheet/ContentTitle";
import ImageModal from "./ImageModal";
import ScrollView from "../ScrollView";
import DataProvider from "../../../data_provider/DataProvider";
import { useSearchParams } from "react-router-dom";
import { ListSearchReqBody } from "../../../data_provider/gateways/MapGateway";
import { zIndex } from "html2canvas/dist/types/css/property-descriptors/z-index";
import { useModal } from "../../modal/Modal";
import ModalButtons from "../../modal/ModalButtons";
import icons from "../../../img/icon/icons";
export interface ImageOptions {
    url: string;
    IID: number;
    first?: boolean;
    last?: boolean;
}

interface GridGalleryProps {
    setIsFullView: Dispatch<SetStateAction<boolean>>;
    xid: number;
    isCreator:boolean;
    // 25.07.10 | Kim Young Jun | 플로팅사이드시트에서 지도 화면 모달 열기/닫기 함수 추가
    openMapScreenModal?: (modalProps: any) => void; // 지도 화면 모달 열기 함수
    closeMapScreenModal?: () => void; // 지도 화면 모달 닫기 함수

    selectedMapIdx: number;
}

/**
 * 플로팅사이드시트 장소 이미지 갤러리
 * @author mjkang
 * @param props {@link GridGalleryProps}
 * @returns GridGallery
 */
const GridGallery = (props: GridGalleryProps) => {
    const [imageData, setImageData] = useState<ImageOptions[] | undefined>();
    const [count, setCount] = useState<number>(0);
    const [selected, setSelected] = useState<number>(-1);
    const [showModal, setShowModal] = useState(false);
    const [deletedItem, setDeletedItem] = useState<{ key: string; value: any }>();
    
    // 25.07.10 | Kim Young Jun | 플로팅사이드시트에서 지도 화면 모달 열기/닫기 함수 추가
    const { openMapScreenModal, closeMapScreenModal } = props;

    // 25.09.10 | Kim Young Jun | mapid값 오버랩 도 받을 수 있도록 수정
    const [params] = useSearchParams();

    const mapid = useMemo(() => {        
        // overlap 파라미터가 있는 경우 overlap된 지도 ID들 중에서 선택
        const overlapParam = params.get("overlap");
        if (overlapParam) {
            // overlap 파라미터에 여러 개의 mapid가 ,로 구분되어 있을 수 있음
            const overlapIds = overlapParam.split(',');
            
            return overlapIds[props.selectedMapIdx - 1];
        }
        
        // 기본적으로는 id 파라미터 사용
        return params.get("id") as string;
    }, [params]);

    useEffect(() => {
        props.setIsFullView(true);

        return () => {
            props.setIsFullView(false);
        };
    }, []);

    useEffect(() => {
        if (!showModal) setSelected(-1);
    }, [showModal]);

    const reqImages = useCallback(
        async (options?: ListSearchReqBody) => {
            const res = await DataProvider.GetPlaceImageList({ mapid, XID: props.xid.toString() }, options);
            res.flag && setImageData(res.result.list);
            res.result.count && setCount(res.result.count);
            return res;
        },
        [props.xid]
    );

    const { openModal } = useModal();

    const onDeleteImage = (iid:number) => {       
        
        if(props.isCreator){
            openMapScreenModal && openMapScreenModal({
                title:"안내",
                children:"이미지를 삭제하시겠습니까?",
                modalButtons: (
                    <ModalButtons
                        buttonCount={1}
                        buttons={[
                            {label: "취소",color: "Secondary",onClick: () => {closeMapScreenModal && closeMapScreenModal(); return;},},
                            {label: "삭제",color: "Danger",onClick: async () => {
                                const formData = new FormData();
                                formData.append("delete",iid.toString());
                                const result = await DataProvider.UpdatePlaceInfo({ mapid: mapid, XID: props.xid.toString()}, formData);
                                if (result.flag) {
                                    setDeletedItem({ key: "IID", value: iid });
                                    closeMapScreenModal && closeMapScreenModal();
                                } else {
                                    // result.status && result.status === 403 && openMapScreenModal && openMapScreenModal({
                                    //     title: "경고",
                                    //     children: "삭제 권한이 없습니다.",
                                    //     modalButtons: ( <ModalButtons buttonCount={1} buttons={[{label: "확인",color: "Danger",onClick: () => {closeMapScreenModal && closeMapScreenModal();},},]}/> ),
                                    // });
                                    // 에러 상황 별 화면 처리

                                    // hmchoi 오류별 처리 구현
                                    switch (result.status) {
                                        case 403:
                                            openModal({title: "오류", children: "이미지를 삭제할 권한이 없습니다." }); break;
                                        case 404:
                                            openModal({title: "오류", children: "이미 삭제되었거나 존재하지 않는 이미지입니다." }); break;
                                        case 500:
                                            openModal({title: "오류", children: "이미지를 처리하는 중 서버 오류가 발생했습니다." }); break;
                                        default:
                                            openModal({ title: "오류", children: result.desc ?? "일시적인 오류가 발생했습니다." });
                                    }

                                    return;
                                }
                                    // setSelectedIdx(-1);
                            },},
                        ]}
                    />
                ),
            })
        }else{
            openMapScreenModal && openMapScreenModal({
                title:"안내",
                children:"삭제 권한이 없습니다.",
                modalButtons: (
                    <ModalButtons
                        buttonCount={1}
                        buttons={[
                            {label: "확인",color: "Primary",onClick: () => {closeMapScreenModal && closeMapScreenModal();return;},},
                        ]}
                    />
                ),
            })
        }
    }

    const renderImages = (image: ImageOptions, idx: number) => {
        const clickImage = () => {
            setShowModal(true);
            setSelected(idx);
        };
        return (
            <>
                <div className={styles.imagecontainer} key={idx}>
                    <img key={idx} src={image.url} onClick={clickImage} />
                    <div className={styles.deletebutton} onClick={() => {onDeleteImage(image.IID);}} >
                        <div className={styles.deleteicon} style={{backgroundImage:`url(${icons["trash_white"]})`}}/>
                    </div>
                    
                </div>
            </>
        
        );
    };

    return (
        <div className={styles.container}>
            <ContentTitle title="사진" count={count} />
            <ScrollView 
                renderItem={renderImages} 
                getData={reqImages} 
                deleteItem={deletedItem}
            />
            {
                imageData && (
                <ImageModal showModal={showModal} setShowModal={setShowModal} images={imageData} selected={selected} setSelected={setSelected} />
            )}
        </div>
    );
};

export default GridGallery;
